import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Phone, Clock, BadgeCheck, Send, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import SectionHeading from "@/components/SectionHeading";
import { API, PHONE_DISPLAY, PHONE_TEL, APPLIANCE_OPTIONS } from "@/constants/site";

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-navy placeholder:text-slate-400 outline-none transition-[border-color,box-shadow] duration-200 focus:border-rescue focus:ring-2 focus:ring-red-600/15";

export default function RequestForm() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    address: "",
    appliance_type: "",
    brand: "",
    problem: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/leads`, form);
      setSuccess(data.lead_id);
      toast.success("Request received — we'll call you back shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please call us directly instead.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="request-service" className="bg-[#FAFAFA] py-20 sm:py-28" data-testid="request-service-section">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-4 sm:px-6 lg:grid-cols-12 lg:gap-10 lg:px-8">
        <div className="lg:col-span-5">
          <SectionHeading
            number="07"
            caption="Book In 60 Seconds"
            title="Request Service Online."
            sub="Fill out the form and a dispatcher will call you back to lock in your same-day window — with the $80 service call waived when you repair with us."
          />

          <div className="-mt-4 space-y-4">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="flex items-center gap-4 rounded-2xl border border-red-100 bg-red-50 p-5"
              data-testid="form-incentive-card"
            >
              <BadgeCheck className="h-8 w-8 shrink-0 text-rescue" />
              <p className="text-sm font-semibold text-navy">
                Your <span className="text-rescue">$80 diagnostic visit is waived in full</span> when
                you approve the repair during the technician's visit.
              </p>
            </motion.div>

            <motion.a
              href={PHONE_TEL}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="group flex items-center justify-between gap-4 rounded-2xl bg-navy p-5 text-white transition-transform duration-200 hover:-translate-y-0.5"
              data-testid="form-call-card"
            >
              <div className="flex items-center gap-4">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-rescue">
                  <Phone className="h-5 w-5" />
                </span>
                <div>
                  <p className="font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">
                    Prefer to talk?
                  </p>
                  <p className="font-display text-lg font-bold">{PHONE_DISPLAY}</p>
                </div>
              </div>
              <span className="text-xs font-semibold text-slate-300 transition-colors group-hover:text-white">
                Tap to call
              </span>
            </motion.a>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5"
            >
              <Clock className="h-8 w-8 shrink-0 text-rescue" />
              <div>
                <p className="text-sm font-bold text-navy">Open 7 days a week, 7AM – 10PM</p>
                <p className="mt-0.5 text-xs text-slate-500">
                  Same-day appointments available across Toronto & the GTA.
                </p>
              </div>
            </motion.div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-7"
        >
          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-xl shadow-slate-900/5 sm:p-9">
            {success ? (
              <div className="flex flex-col items-center py-14 text-center" data-testid="request-form-success">
                <CheckCircle2 className="h-16 w-16 text-green-600" />
                <h3 className="mt-6 font-display text-2xl font-bold text-navy">
                  Request Received — You're In The Queue.
                </h3>
                <p className="mt-3 max-w-md text-sm leading-relaxed text-slate-600 sm:text-base">
                  Your reference is <span className="font-mono font-bold text-rescue">#{success}</span>.
                  A dispatcher will call you back shortly to confirm your same-day window.
                  Need us faster?
                </p>
                <a
                  href={PHONE_TEL}
                  className="mt-6 flex items-center gap-2.5 rounded-full bg-rescue px-7 py-3.5 text-sm font-bold text-white shadow-lg shadow-red-600/25 transition-colors hover:bg-rescue-dark"
                  data-testid="success-call-button"
                >
                  <Phone className="h-4 w-4" /> Call {PHONE_DISPLAY}
                </a>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="request-service-form">
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="rf-name" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">
                      Full Name *
                    </label>
                    <input
                      id="rf-name"
                      data-testid="request-form-name-input"
                      required
                      value={form.name}
                      onChange={set("name")}
                      placeholder="John Smith"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label htmlFor="rf-phone" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">
                      Phone Number *
                    </label>
                    <input
                      id="rf-phone"
                      data-testid="request-form-phone-input"
                      required
                      type="tel"
                      value={form.phone}
                      onChange={set("phone")}
                      placeholder="416-555-0123"
                      className={inputCls}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="rf-address" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">
                    Service Address & City *
                  </label>
                  <input
                    id="rf-address"
                    data-testid="request-form-address-input"
                    required
                    value={form.address}
                    onChange={set("address")}
                    placeholder="123 Yonge St, Toronto, ON"
                    className={inputCls}
                  />
                </div>

                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="rf-type" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">
                      Appliance Type *
                    </label>
                    <select
                      id="rf-type"
                      data-testid="request-form-appliance-type-select"
                      required
                      value={form.appliance_type}
                      onChange={set("appliance_type")}
                      className={inputCls}
                    >
                      <option value="" disabled>
                        Select appliance…
                      </option>
                      {APPLIANCE_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="rf-brand" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">
                      Brand
                    </label>
                    <input
                      id="rf-brand"
                      data-testid="request-form-brand-input"
                      value={form.brand}
                      onChange={set("brand")}
                      placeholder="Samsung, LG, Whirlpool…"
                      className={inputCls}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="rf-problem" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">
                    Describe the Problem *
                  </label>
                  <textarea
                    id="rf-problem"
                    data-testid="request-form-problem-textarea"
                    required
                    rows={4}
                    value={form.problem}
                    onChange={set("problem")}
                    placeholder="e.g. Fridge stopped cooling yesterday, freezer still works, hearing a clicking sound…"
                    className={`${inputCls} resize-none`}
                  />
                </div>

                <button
                  type="submit"
                  data-testid="request-form-submit-button"
                  disabled={loading}
                  className="group flex w-full items-center justify-center gap-2.5 rounded-full bg-rescue py-4 text-base font-bold text-white shadow-xl shadow-red-600/25 transition-[background-color,transform] duration-200 hover:bg-rescue-dark hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {loading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <Send className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
                  )}
                  {loading ? "Sending…" : "Submit Repair Request"}
                </button>

                <p className="text-center text-xs text-slate-400">
                  No spam, ever. We only call to confirm your appointment.
                </p>
              </form>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
